import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useList } from '../api/queries'
import { OfflineIndicator, useAutoSync } from '../features/field/pending'
import type { Alert } from '../api/types'
import { humanize } from '../lib/format'
import { navFor, type NavItem } from '../lib/permissions'
import { Icon } from './Icon'
import { cx } from './ui'
import { UpdateBanner } from './UpdateBanner'

function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) close() }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open, close])
  return ref
}

function Logo() {
  return (
    <Link to="/" className="brand" aria-label="Drive Tech - inicio">
      <img src="/brand/drivetech-mark-150.png" width="32" height="32" alt="" />
      <span>Drive Tech</span>
    </Link>
  )
}

function Popover({ label, button, children, align = 'right' }: { label: string; button: ReactNode; children: ReactNode; align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false)
  const ref = useDismiss(open, () => setOpen(false))
  const location = useLocation()
  useEffect(() => setOpen(false), [location.pathname])
  return (
    <div className="popover" ref={ref}>
      <button type="button" className="popover__button" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen((o) => !o)}>{button}</button>
      {open ? <div role="menu" className={cx('popover__menu', `popover__menu--${align}`)} onClick={() => setOpen(false)}>{children}</div> : null}
    </div>
  )
}

function MenuLink({ item }: { item: NavItem }) {
  return (
    <NavLink to={item.path} role="menuitem" className="menu-item" end={item.path === '/'}>
      <Icon name={item.icon} size={18} />
      {item.label}
    </NavLink>
  )
}

export function Shell() {
  const { user, logout } = useAuth()
  const nav = navFor(user?.role)
  const alerts = useList<Alert>('/api/alerts/', { status: 'open', page_size: 1 })
  const open = alerts.data?.count
  const [drawer, setDrawer] = useState(false)
  const location = useLocation()
  useEffect(() => setDrawer(false), [location.pathname])
  const personalName = user ? `${user.first_name} ${user.last_name}`.trim() || user.username : ''
  const displayName = user?.client_name?.trim() || personalName
  const initials = (displayName || '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()
  const all = [...nav.primary, ...nav.more]
  useAutoSync(user?.id ?? 0)

  return (
    <div className="app">
      <a className="skip-link" href="#main">Saltar al contenido</a>
      <div className="frame">
        <header className="topbar">
          <Logo />
          <nav aria-label="Principal" className="pillnav">
            {nav.primary.map((item) => (
              <NavLink key={item.key} to={item.path} end={item.path === '/'} className="pillnav__tab">
                <Icon name={item.icon} size={16} />
                <span>{item.label}</span>
              </NavLink>
            ))}
            {nav.more.length ? (
              <Popover label="Más páginas" align="left" button={<><Icon name="more" size={16} /><span>Más</span></>}>
                {nav.more.map((item) => <MenuLink key={item.key} item={item} />)}
              </Popover>
            ) : null}
          </nav>
          <div className="topbar__right">
            <Link to="/alerts" className="icon-btn icon-btn--bell" aria-label={open === undefined ? 'Alertas' : `Alertas (${open} ${open === 1 ? 'abierta' : 'abiertas'})`}>
              <Icon name="bell" size={20} />
              {open ? <span className="bell-dot mono" aria-hidden="true">{open > 99 ? '99+' : open}</span> : null}
            </Link>
            <Popover label="Menú de cuenta" button={<><span className="avatar" aria-hidden="true">{initials}</span><span className="user-chip__text"><span className="user-chip__name">{displayName}</span><span className="user-chip__role">{humanize(user?.role)}</span></span><Icon name="chevron-down" size={14} /></>}>
              <div className="menu-head"><strong>{displayName}</strong><span className="muted">{humanize(user?.role)}</span></div>
              <NavLink to="/drafts" role="menuitem" className="menu-item"><Icon name="save" size={18} />Borradores en este dispositivo</NavLink>
              <button type="button" role="menuitem" className="menu-item" onClick={() => void logout()}><Icon name="logout" size={18} />Cerrar sesión</button>
            </Popover>
          </div>
        </header>
        <main id="main" className="main" tabIndex={-1}>
          <UpdateBanner />
          <OfflineIndicator />
          <Outlet />
        </main>
      </div>

      <nav aria-label="Móvil" className="bottombar">
        {nav.mobile.map((item) => (
          <NavLink key={item.key} to={item.path} end={item.path === '/'} className="bottombar__tab">
            <Icon name={item.icon} size={22} />
            <span>{item.label}</span>
          </NavLink>
        ))}
        <button type="button" className="bottombar__tab" onClick={() => setDrawer(true)} aria-haspopup="dialog">
          <Icon name="menu" size={22} />
          <span>Menú</span>
        </button>
      </nav>
      {drawer ? (
        <div className="drawer-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setDrawer(false) }} onKeyDown={(e) => { if (e.key === 'Escape') setDrawer(false) }}>
          <div className="drawer" role="dialog" aria-modal="true" aria-label="Todas las páginas">
            <div className="drawer__head"><strong>{displayName}</strong><span className="muted">{humanize(user?.role)}</span><button type="button" className="icon-btn" aria-label="Cerrar menú" onClick={() => setDrawer(false)}><Icon name="x" size={18} /></button></div>
            <ul className="drawer__list">
              {all.map((item) => (
                <li key={item.key}><NavLink to={item.path} end={item.path === '/'} className="menu-item"><Icon name={item.icon} size={18} />{item.label}</NavLink></li>
              ))}
            </ul>
            <button type="button" className="btn btn--ghost" onClick={() => void logout()}><Icon name="logout" size={16} /> Cerrar sesión</button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
